import { Link } from "react-router-dom";
import ThemedIcon from "@components/common/ThemedIcon";
import MicrosoftLogo from "@components/common/MicrosoftLogo";
import useInView from "@hooks/useInView";
import useCountUp from "@hooks/useCountUp";
import { HERO_STATS, HERO_IMAGE } from "@data/constants";

function StatCounter({ end, suffix = "", label, icon, text, highlight }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const count = useCountUp(end || 0, inView, 2000);

  return (
    <div
      ref={ref}
      className={`p-4 lg:p-5 text-center group ${
        highlight
          ? "glass-card relative border-konecta-orange/40 bg-konecta-orange/[0.08] ring-1 ring-konecta-orange/20"
          : "glass-card"
      }`}
    >
      {icon === "microsoft" ? (
        <div className="mb-2 opacity-60 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <MicrosoftLogo size={22} />
        </div>
      ) : (
        <ThemedIcon
          name={icon}
          size={22}
          className="mb-2 opacity-60 group-hover:opacity-100 transition-opacity"
        />
      )}
      <div
        className="font-heading font-extrabold text-konecta-white leading-none"
        style={{
          fontSize: text
            ? "clamp(1.1rem, 2vw, 2rem)"
            : "clamp(1.5rem, 2.5vw, 2.5rem)",
        }}
      >
        <span className="text-gradient-orange">
          {text || `${count}${suffix}`}
        </span>
      </div>
      <div
        className={`text-xs mt-2 uppercase tracking-wider ${
          highlight ? "text-konecta-orange font-bold" : "text-white/70"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

export default function Hero() {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-start overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-konecta-black/50 via-konecta-black/40 to-konecta-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-konecta-black/60 via-transparent to-transparent" />
      </div>

      {/* Floating glow orbs */}
      <div className="absolute top-1/4 right-1/4 w-[200px] h-[200px] md:w-[300px] md:h-[300px] lg:w-[400px] lg:h-[400px] bg-konecta-orange/10 rounded-full blur-[120px] animate-float pointer-events-none" />
      <div className="absolute bottom-1/3 left-[20%] w-[160px] h-[160px] md:w-[220px] md:h-[220px] lg:w-[300px] lg:h-[300px] bg-konecta-slate/8 rounded-full blur-[100px] animate-float-slow pointer-events-none" />

      {/* Main content */}
      <div className="relative z-10 content-px pt-28 pb-16">
        {/* Eyebrow with glass pill */}
        <div
          className={`inline-flex items-center gap-3 mb-8 px-5 py-2.5 rounded-full transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{
            background: "rgba(244, 129, 32, 0.08)",
            border: "1px solid rgba(244, 129, 32, 0.2)",
            backdropFilter: "blur(20px)",
          }}
        >
          <div className="w-2 h-2 rounded-full bg-konecta-orange animate-pulse-dot" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-konecta-orange">
            Technology - Media - Telecommunications
          </span>
        </div>

        {/* Hero headline */}
        <h1
          className={`font-heading font-extrabold text-konecta-white leading-[0.95] tracking-tight max-w-5xl transition-all duration-1000 delay-200 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ fontSize: "clamp(2.2rem, 7vw, 6.5rem)" }}
        >
          We Build Networks.
          <br />
          We Power <em className="not-italic text-gradient-orange">Data.</em>
        </h1>

        {/* Sub-copy */}
        <p
          className={`text-base lg:text-lg text-white/80 mt-8 max-w-xl leading-relaxed transition-all duration-1000 delay-400 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          We are a Level 1 B-BBEE, 100% women-owned technology company
          delivering AI platforms, digital infrastructure, and enterprise
          technology solutions across South Africa and the continent.
        </p>

        {/* CTAs */}
        <div
          className={`flex gap-4 mt-10 flex-wrap transition-all duration-1000 delay-500 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <Link to="/contact" className="btn-primary">
            Request a Proposal
          </Link>
          <Link to="/contact" className="btn-secondary">
            Connect With Us
          </Link>
        </div>

        {/* Stats strip — inside content block so they're always visible */}
        <div className="mt-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {HERO_STATS.map((stat) => (
              <StatCounter key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center p-2">
          <div className="w-1 h-2.5 rounded-full bg-konecta-orange animate-bounce" />
        </div>
      </div>
    </section>
  );
}
